/**
 * Brand kits (docs/15-product/brand-kits.md §3): the org's brand in one row,
 * injected into every brief as data. One default per org; deletes are soft.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { BrandKit, BrandKitBody, BrandKits } from "./types";

export const BRAND_KITS_KEY = ["brand-kits"] as const;

export function useBrandKits() {
  return useQuery({
    queryKey: BRAND_KITS_KEY,
    queryFn: () => api<BrandKits>("/v1/brand-kits"),
  });
}

function useAfterWrite() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: BRAND_KITS_KEY });
  };
}

/** `POST /v1/brand-kits` → 201. A `validation_error` names the field in
 *  `detail.field` (e.g. `palette[0].hex`) and says what it must be. */
export function useCreateBrandKit() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (body: BrandKitBody) =>
      api<BrandKit>("/v1/brand-kits", { method: "POST", body }),
    onSettled: after,
  });
}

/** `PATCH /v1/brand-kits/{id}`: only the sent fields change. */
export function useUpdateBrandKit() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<BrandKitBody> & { id: string }) =>
      api<BrandKit>(`/v1/brand-kits/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSettled: after,
  });
}

/** `POST /v1/brand-kits/{id}/default`: the kit every brief starts from when
 *  a job names none. */
export function useSetDefaultBrandKit() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (id: string) =>
      api<BrandKit>(`/v1/brand-kits/${encodeURIComponent(id)}/default`, {
        method: "POST",
      }),
    onSettled: after,
  });
}

/** `DELETE /v1/brand-kits/{id}/default`: the org is left with no default
 *  kit, so a new ad has no brand kit unless one is picked. */
export function useRemoveDefaultBrandKit() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (id: string) =>
      api<BrandKit>(`/v1/brand-kits/${encodeURIComponent(id)}/default`, {
        method: "DELETE",
      }),
    onSettled: after,
  });
}

/** `DELETE /v1/brand-kits/{id}` → 204 (soft; jobs keep their kit id). */
export function useDeleteBrandKit() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/v1/brand-kits/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSettled: after,
  });
}
